import { useState, useMemo } from 'react'
import useDealStore from '../store/dealStore'
import '../styles/module14.css'

const money = (v) => `$${Math.round(Number(v) || 0).toLocaleString()}`
const today = () => new Date().toISOString().split('T')[0]
const fmtDate = (d) => d ? new Date(d + 'T12:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '—'
const addDays = (d, n) => { const dt = new Date(d + 'T12:00:00'); dt.setDate(dt.getDate() + n); return dt.toISOString().split('T')[0] }

// ── Closing steps ─────────────────────────────────────────────────────────────
const CLOSING_STEPS = [
  { id: 'loan_commitment', label: 'Final Loan Commitment', icon: '🏦', phase: 'Financing', desc: 'Receive written loan commitment letter from lender confirming final approval.' },
  { id: 'cd_received', label: 'Closing Disclosure Received', icon: '📄', phase: 'Financing', desc: 'Lender issues Closing Disclosure (CD) — 3 business day review period required.' },
  { id: 'cd_approved', label: 'CD Reviewed & Approved', icon: '', phase: 'Financing', desc: 'Buyer reviews and confirms all loan terms, fees, and cash-to-close on the CD.' },
  { id: 'wire_downpayment', label: 'Down Payment Wired', icon: '💸', phase: 'Funds', desc: 'Buyer wires down payment to title/escrow company per closing instructions.' },
  { id: 'wire_lender', label: 'Loan Funds Received', icon: '🏛️', phase: 'Funds', desc: 'Lender wires loan proceeds to escrow; confirm receipt.' },
  { id: 'stakeholders_conf', label: 'All Parties Confirmed', icon: '🤝', phase: 'Coordination', desc: 'Buyer, seller, lender, and title company all confirmed for closing date/time.' },
  { id: 'docs_signed', label: 'Documents Signed', icon: '📝', phase: 'Signing', desc: 'All loan documents, deed, and title paperwork executed by all parties.' },
  { id: 'funds_disbursed', label: 'Funds Disbursed', icon: '💰', phase: 'Disbursement', desc: 'Escrow disburses proceeds: seller payoff, commissions, fees, and surplus.' },
  { id: 'deed_recorded', label: 'Deed Recorded', icon: '🏛️', phase: 'Recording', desc: 'Deed recorded with county recorder — official ownership transfer complete.' },
  { id: 'title_issued', label: 'Title Insurance Issued', icon: '🎉', phase: 'Recording', desc: "Owner's and lender's title insurance policies issued. Keys transferred." },
]

const PHASES = ['Financing', 'Funds', 'Coordination', 'Signing', 'Disbursement', 'Recording']

function calcProgress(completed) {
  return Math.round((completed.length / CLOSING_STEPS.length) * 100)
}

function getStepStatuses(completed) {
  let foundActive = false
  return CLOSING_STEPS.map(s => {
    if (completed.includes(s.id)) return 'done'
    if (!foundActive) { foundActive = true; return 'active' }
    return 'pending'
  })
}

function StakeholderCard({ label, icon, value, onChange }) {
  return (
    <div className="m14-stakeholder-card">
      <div className="m14-stakeholder-icon">{icon}</div>
      <div className="m14-stakeholder-label">{label}</div>
      <select className="m14-stakeholder-select" value={value} onChange={e => onChange(e.target.value)}>
        <option value="">— Select status —</option>
        <option value="not_contacted">Not Contacted</option>
        <option value="contacted">Contacted</option>
        <option value="confirmed">Confirmed</option>
        <option value="signed">Signed</option>
      </select>
    </div>
  )
}

export default function Module14Page() {
  const { dealState, updateDealState, updateAvailableModules, forceNavigateToModule } = useDealStore()

  const raw = dealState.rawDealData || {}
  const fin = dealState.extractedFinancials || {}
  const uw = dealState.underwritingMetrics || {}
  const loi = dealState.loiDocument || {}
  const escrow = dealState.escrowDetails || {}
  const saved = dealState.closingDetails || {}
  const dueDiligence = dealState.dueDiligence || {}
  const ownershipCosts = dealState.ownershipCosts || {}
  const dueDiligenceCleared = !!dueDiligence.finalClearanceConfirmed && dueDiligence.readyToClose !== false
  const dueDiligenceBlockers = dueDiligence.blockers || []

  const propName = raw.location?.address || 'Subject Property'
  const location = raw.location?.city && raw.location?.state ? `${raw.location.city}, ${raw.location.state}` : ''
  const price = Number(raw.price) || 0
  const ltv = Number(uw.debtService?.ltv) || 0.75
  const loanAmt = Number(uw.debtService?.loanAmount) || Math.round(price * ltv)
  const downPmt = price - loanAmt
  const rate = Number(uw.debtService?.interestRate) || 6.75
  const noi = Number(fin.noi) || 0

  // Estimate closing costs ~2.5% of price
  const closingCosts = Math.round(price * 0.025)
  const cashToClose = downPmt + closingCosts

  // Restore saved state
  const [completed, setCompleted] = useState(saved.completedSteps || [])
  const [activeTab, setActiveTab] = useState('workflow')
  const [closingDate, setClosingDate] = useState(saved.closingDate || addDays(today(), 21))
  const [cdDate, setCdDate] = useState(saved.cdDate || addDays(today(), 7))
  const [lenderName, setLenderName] = useState(saved.lenderName || (dealState.lenderMatches?.topRecommendation || ''))
  const [loanRef, setLoanRef] = useState(saved.loanRef || '')
  const [notes, setNotes] = useState(saved.notes || {})
  const [stakeholders, setStakeholders] = useState(saved.stakeholders || { buyer: '', seller: '', lender: '', titleCompany: '' })
  const [wireAmt, setWireAmt] = useState(saved.wireAmt || String(cashToClose))
  const [sellerPayout, setSellerPayout] = useState(saved.sellerPayout || String(Math.round(price * 0.97)))
  const [saving, setSaving] = useState(false)
  const [done, setDone] = useState(!!(saved.recording?.deedRegistered))

  // ── Itemized Closing Cost Breakdown ────────────────────────────────────
  const WIRE_STEPS = (() => [
    { id: 'w1', label: 'Call the title/escrow company directly using a phone number from their official website — not from any email.' },
    { id: 'w2', label: 'Confirm wire instructions verbally before initiating the transfer.' },
    { id: 'w3', label: 'Verify every digit of the routing number and account number independently.' },
    { id: 'w4', label: 'Confirm the receiving bank name matches your records exactly.' },
    { id: 'w5', label: 'Never wire based solely on emailed instructions — fraudsters intercept closing emails.' },
    { id: 'w6', label: 'Call your bank to confirm the wire was received by the intended recipient within 1 hour.' },
    { id: 'w7', label: 'Ensure your email and phone accounts use two-factor authentication during the transaction.' },
    { id: 'w8', label: 'If anything seems off, halt the wire and call the escrow officer directly to verify.' },
  ])()

  const [costItems, setCostItems] = useState(() => {
    const p = price, l = loanAmt, r = rate
    return [
      { id: 'c01', cat: 'Lender Fees', label: 'Origination Fee (1%)', amount: Math.round(l * 0.01) },
      { id: 'c02', cat: 'Lender Fees', label: 'Underwriting Fee', amount: 1495 },
      { id: 'c03', cat: 'Lender Fees', label: 'Appraisal Fee', amount: 650 },
      { id: 'c04', cat: 'Lender Fees', label: 'Credit Report', amount: 45 },
      { id: 'c05', cat: 'Title / Escrow', label: 'Title Insurance (Lender)', amount: Math.round(l * 0.004) },
      { id: 'c06', cat: 'Title / Escrow', label: 'Title Insurance (Owner)', amount: Math.round(p * 0.003) },
      { id: 'c07', cat: 'Title / Escrow', label: 'Escrow / Settlement Fee', amount: 1200 },
      { id: 'c08', cat: 'Title / Escrow', label: 'Attorney Fee', amount: 800 },
      { id: 'c09', cat: 'Gov. Fees', label: 'Recording Fee', amount: 250 },
      { id: 'c10', cat: 'Gov. Fees', label: 'Transfer Tax', amount: Math.round(p * 0.001) },
      { id: 'c11', cat: 'Prepaids', label: 'Homeowners Insurance (1yr)', amount: Math.round(p * 0.006) },
      { id: 'c12', cat: 'Prepaids', label: 'Property Tax Escrow (2mo)', amount: Math.round(p * 0.01 / 6) },
      { id: 'c13', cat: 'Prepaids', label: 'Prepaid Interest (15 days)', amount: Math.round(l * (r / 100 / 365) * 15) },
      { id: 'c14', cat: 'Other', label: 'Survey Fee', amount: 600 },
      { id: 'c15', cat: 'Other', label: 'Inspection Fee', amount: 500 },
      { id: 'c16', cat: 'Other', label: 'Environmental Report', amount: 350 },
    ]
  })
  const [wireChecks, setWireChecks] = useState([])

  const totalItemizedCost = useMemo(() => costItems.reduce((s, c) => s + Number(c.amount), 0), [costItems])
  const itemizedCostCats = useMemo(() => [...new Set(costItems.map((c) => c.cat))], [costItems])

  const progress = calcProgress(completed)
  const stepStatuses = useMemo(() => getStepStatuses(completed), [completed])
  const allDone = completed.length === CLOSING_STEPS.length
  const deedRecorded = completed.includes('deed_recorded')

  function toggleStep(id) {
    if (!dueDiligenceCleared) return
    const idx = CLOSING_STEPS.findIndex(s => s.id === id)
    // Must complete in order — can only check if all prior are done
    const prior = CLOSING_STEPS.slice(0, idx).map(s => s.id)
    const allPriorDone = prior.every(p => completed.includes(p))
    if (!allPriorDone && !completed.includes(id)) return
    setCompleted(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id])
  }

  function setNote(key, val) { setNotes(prev => ({ ...prev, [key]: val })) }
  function setStakeholder(key, val) { setStakeholders(prev => ({ ...prev, [key]: val })) }

  function handleSave() {
    updateDealState({
      closingDetails: {
        ...saved,
        completedSteps: completed,
        closingDate,
        cdDate,
        lenderName,
        loanRef,
        notes,
        stakeholders,
        wireAmt,
        sellerPayout,
        dueDiligenceClearance: {
          cleared: dueDiligenceCleared,
          clearedAt: dueDiligence.clearedAt || null,
          blockers: dueDiligenceBlockers,
        },
        loanApproval: completed.includes('loan_commitment') ? loanRef || 'confirmed' : null,
        closingDisclosure: completed.includes('cd_approved') ? 'reviewed' : null,
        stakeholderCoordination: stakeholders,
        fundsVerification: {
          downPayment: completed.includes('wire_downpayment') ? wireAmt : null,
          loanFunds: completed.includes('wire_lender') ? String(loanAmt) : null,
          escrowBalance: escrow.escrowNumber || null
        },
        documentSigning: completed.includes('docs_signed') ? new Date().toISOString() : null,
        fundDisbursement: {
          sellerPayout: completed.includes('funds_disbursed') ? sellerPayout : null,
          feesDistribution: completed.includes('funds_disbursed') ? String(closingCosts) : null,
        },
        recording: {
          deedRegistered: completed.includes('deed_recorded') || null,
          ownershipTransferred: completed.includes('deed_recorded') || null,
          titleInsuranceIssued: completed.includes('title_issued') || null,
        },
        savedAt: new Date().toISOString()
      }
    })
  }

  function handleComplete() {
    if (!dueDiligenceCleared || !deedRecorded) return
    setSaving(true)
    updateDealState({
      closingDetails: {
        ...saved,
        completedSteps: completed,
        closingDate,
        cdDate,
        lenderName,
        loanRef,
        notes,
        stakeholders,
        wireAmt,
        sellerPayout,
        dueDiligenceClearance: {
          cleared: dueDiligenceCleared,
          clearedAt: dueDiligence.clearedAt || null,
          blockers: dueDiligenceBlockers,
        },
        loanApproval: loanRef || 'confirmed',
        closingDisclosure: 'reviewed',
        stakeholderCoordination: stakeholders,
        fundsVerification: {
          downPayment: wireAmt,
          loanFunds: String(loanAmt),
          escrowBalance: escrow.escrowNumber || null
        },
        documentSigning: new Date().toISOString(),
        fundDisbursement: {
          sellerPayout,
          feesDistribution: String(closingCosts)
        },
        recording: {
          deedRegistered: true,
          ownershipTransferred: true,
          titleInsuranceIssued: completed.includes('title_issued') || true,
        },
        completedAt: new Date().toISOString()
      },
      pipelineStage: Math.max(dealState.pipelineStage || 1, 15),
      pipelineStatus: 'in_progress'
    })
    updateAvailableModules()
    setDone(true)
    setTimeout(() => {
      setSaving(false)
      forceNavigateToModule(16)
    }, 900)
  }

  if (done) {
    return (
      <div className="m14-complete-screen">
        <div className="m14-complete-card">
          <div className="m14-complete-icon">🎉</div>
          <h2>Closed!</h2>
          <p>Deed recorded. Ownership transferred. Proceeding to Asset Management.</p>
          <div className="m14-complete-badge">Module 14 Complete</div>
          <button className="m14-proceed-btn" onClick={() => forceNavigateToModule(16)}>Go to Asset Plan &amp; Hold</button>
        </div>
      </div>
    )
  }

  return (
    <div className="m14-page">

      {/* ── Header ─────────────────────────────────────────────── */}
      <div className="m14-header">
        <div className="m14-header-left">
          <div className="m14-module-badge">Module 14</div>
          <div>
            <h1>Financing, Closing &amp; Recording</h1>
            <p className="m14-sub">{propName}{location && ` · ${location}`} · Escrow {escrow.escrowNumber || '—'}</p>
          </div>
        </div>
        <div className="m14-header-right">
          <div className="m14-closing-date-pill">
            <span className="m14-pill-label">Closing</span>
            <span className="m14-pill-val">{fmtDate(closingDate)}</span>
          </div>
          <button className="m14-save-btn" onClick={handleSave}>Save Progress</button>
        </div>
      </div>

      {/* ── Progress bar ───────────────────────────────────────── */}
      <div className="m14-progress-row">
        <div className="m14-progress-bar-wrap">
          <div className="m14-progress-bar" style={{ width: `${progress}%` }} />
        </div>
        <span className="m14-progress-label">{completed.length}/{CLOSING_STEPS.length} steps complete ({progress}%)</span>
      </div>

      {!dueDiligenceCleared && (
        <div className="m14-dd-gate">
          <div>
            <h3>Due Diligence Must Be Cleared First</h3>
            <p>Financing, closing, signing, disbursement, and recording are locked until Module 13 has no unresolved document, title, city, inspection, financing, insurance, lender, or renegotiation blockers.</p>
          </div>
          <button onClick={() => forceNavigateToModule(14)}>Return to Due Diligence</button>
          {!!dueDiligenceBlockers.length && (
            <div className="m14-dd-gate-list">
              {dueDiligenceBlockers.slice(0, 6).map((blocker) => <span key={blocker}>{blocker}</span>)}
              {dueDiligenceBlockers.length > 6 && <span>+{dueDiligenceBlockers.length - 6} more</span>}
            </div>
          )}
        </div>
      )}

      {/* ── Deal summary strip ─────────────────────────────────── */}
      <div className="m14-deal-strip">
        {[
          { label: 'Purchase Price', val: money(price) },
          { label: 'Loan Amount', val: money(loanAmt) },
          { label: 'Down Payment', val: money(downPmt) },
          { label: 'Interest Rate', val: rate.toFixed(2) + '%' },
          { label: 'Est. Cash to Close', val: money(cashToClose) },
          { label: 'Closing Date', val: fmtDate(closingDate) },
        ].map(s => (
          <div key={s.label} className="m14-strip-item">
            <div className="m14-strip-val">{s.val}</div>
            <div className="m14-strip-label">{s.label}</div>
          </div>
        ))}
      </div>

      {/* ── Tabs ───────────────────────────────────────────────── */}
      <div className="m14-tabs">
        {[
          { id: 'workflow', label: '📋 Closing Workflow' },
          { id: 'financing', label: '🏦 Loan Details' },
          { id: 'coordination', label: '🤝 Coordination' },
          { id: 'funds', label: '💸 Funds & Signing' },
          { id: 'costs', label: '💰 Closing Costs' },
          { id: 'wiresecurity', label: '🔒 Wire Security' },
        ].map(t => (
          <button key={t.id} className={`m14-tab ${activeTab === t.id ? 'active' : ''}`} onClick={() => setActiveTab(t.id)}>
            {t.label}
          </button>
        ))}
      </div>

      <div className="m14-content">

        {/* ── WORKFLOW TAB ───────────────────────────────────── */}
        {activeTab === 'workflow' && (
          <div className="m14-workflow-panel">
            <div className="m14-section-header">
              <h3>Closing Workflow</h3>
              <p>Work through each step in order. Steps must be completed sequentially — each builds on the last.</p>
            </div>

            {/* Phase swimlanes */}
            {PHASES.map(phase => {
              const phaseSteps = CLOSING_STEPS.filter(s => s.phase === phase)
              const phaseIdx = phaseSteps.map(s => CLOSING_STEPS.findIndex(x => x.id === s.id))
              const phaseDone = phaseSteps.filter(s => completed.includes(s.id)).length
              return (
                <div key={phase} className="m14-phase-block">
                  <div className="m14-phase-header">
                    <span className="m14-phase-name">{phase}</span>
                    <span className="m14-phase-count">{phaseDone}/{phaseSteps.length}</span>
                  </div>
                  <div className="m14-phase-steps">
                    {phaseSteps.map((step, i) => {
                      const globalIdx = CLOSING_STEPS.findIndex(x => x.id === step.id)
                      const status = stepStatuses[globalIdx]
                      const isDone = status === 'done'
                      const isActive = status === 'active'
                      return (
                        <div
                          key={step.id}
                          className={`m14-step ${isDone ? 'done' : ''} ${isActive ? 'active' : ''} ${status === 'pending' ? 'pending' : ''}`}
                          onClick={() => toggleStep(step.id)}
                        >
                          <div className="m14-step-num">
                            {isDone ? '✓' : globalIdx + 1}
                          </div>
                          <div className="m14-step-body">
                            <div className="m14-step-icon">{step.icon}</div>
                            <div className="m14-step-info">
                              <div className="m14-step-label">{step.label}</div>
                              <div className="m14-step-desc">{step.desc}</div>
                            </div>
                          </div>
                          {isDone && <div className="m14-step-check">✓</div>}
                          {isActive && <div className="m14-step-action">Mark Done →</div>}
                        </div>
                      )
                    })}
                  </div>
                </div>
              )
            })}
            <p className="m14-workflow-hint">Click the active step to mark it complete and unlock the next.</p>
          </div>
        )}

        {/* ── FINANCING / LOAN TAB ───────────────────────────── */}
        {activeTab === 'financing' && (
          <div className="m14-financing-panel">
            <div className="m14-section-header">
              <h3>Loan Details &amp; Approval</h3>
              <p>Confirm final loan terms and track the commitment letter and Closing Disclosure timeline.</p>
            </div>

            <div className="m14-form-grid">
              <div className="m14-form-group">
                <label>Lender Name</label>
                <input className="m14-input" value={lenderName} onChange={e => setLenderName(e.target.value)} placeholder="e.g. Pacific Premier Bank" />
              </div>
              <div className="m14-form-group">
                <label>Loan Reference / File #</label>
                <input className="m14-input" value={loanRef} onChange={e => setLoanRef(e.target.value)} placeholder="e.g. LN-2024-00842" />
              </div>
              <div className="m14-form-group">
                <label>CD Issued Date</label>
                <input className="m14-input" type="date" value={cdDate} onChange={e => setCdDate(e.target.value)} />
              </div>
              <div className="m14-form-group">
                <label>Closing Date</label>
                <input className="m14-input" type="date" value={closingDate} onChange={e => setClosingDate(e.target.value)} />
              </div>
            </div>

            {/* Loan summary cards */}
            <div className="m14-loan-cards">
              {[
                { label: 'Purchase Price', val: money(price), note: 'Contract price' },
                { label: 'Loan Amount', val: money(loanAmt), note: `${(ltv * 100).toFixed(0)}% LTV` },
                { label: 'Down Payment', val: money(downPmt), note: `${(100 - ltv * 100).toFixed(0)}% equity` },
                { label: 'Interest Rate', val: rate.toFixed(2) + '%', note: 'Per annum' },
                { label: 'Closing Costs', val: money(closingCosts), note: '~2.5% of price' },
                { label: 'Est. Cash to Close', val: money(cashToClose), note: 'Down pmt + costs' },
                ...(ownershipCosts.monthlyDebtService > 0 ? [{ label: 'Monthly Debt Service', val: money(ownershipCosts.monthlyDebtService), note: 'P&I payment' }] : []),
                ...(ownershipCosts.annualInsurance > 0 ? [{ label: `Annual Insurance${ownershipCosts.insuranceIsEstimated ? ' (est.)' : ''}`, val: money(ownershipCosts.annualInsurance), note: ownershipCosts.insuranceIsEstimated ? '0.25% of price' : 'From docs' }] : []),
                ...(ownershipCosts.annualPropertyTax > 0 ? [{ label: 'Annual Property Tax', val: money(ownershipCosts.annualPropertyTax), note: 'Prorated at closing' }] : []),
                ...(ownershipCosts.totalMonthlyOwnershipCost > 0 ? [{ label: 'Total Monthly Cost', val: money(ownershipCosts.totalMonthlyOwnershipCost), note: 'All-in monthly' }] : []),
              ].map(c => (
                <div key={c.label} className="m14-loan-card">
                  <div className="m14-loan-val">{c.val}</div>
                  <div className="m14-loan-label">{c.label}</div>
                  <div className="m14-loan-note">{c.note}</div>
                </div>
              ))}
            </div>

            {/* CD timeline */}
            <div className="m14-cd-timeline">
              <h4>Closing Disclosure Timeline</h4>
              <div className="m14-cd-steps">
                {[
                  { label: 'CD Issued by Lender', date: cdDate, done: completed.includes('cd_received') },
                  { label: '3-Business Day Review', date: addDays(cdDate, 3), done: completed.includes('cd_approved') },
                  { label: 'Closing Day', date: closingDate, done: completed.includes('docs_signed') },
                  { label: 'Recording', date: addDays(closingDate, 1), done: completed.includes('deed_recorded') },
                ].map(step => (
                  <div key={step.label} className={`m14-cd-step ${step.done ? 'done' : ''}`}>
                    <div className="m14-cd-dot" />
                    <div className="m14-cd-step-body">
                      <div className="m14-cd-step-label">{step.label}</div>
                      <div className="m14-cd-step-date">{fmtDate(step.date)}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="m14-form-group full">
              <label>Lender Notes</label>
              <textarea className="m14-textarea" rows={3} value={notes.lender || ''} onChange={e => setNote('lender', e.target.value)} placeholder="Loan conditions, outstanding items, rate lock expiry…" />
            </div>
          </div>
        )}

        {/* ── COORDINATION TAB ───────────────────────────────── */}
        {activeTab === 'coordination' && (
          <div className="m14-coord-panel">
            <div className="m14-section-header">
              <h3>Stakeholder Coordination</h3>
              <p>Confirm all parties are ready for the closing date. Everyone must be confirmed before signing.</p>
            </div>

            <div className="m14-stakeholder-grid">
              {[
                { key: 'buyer', label: 'Buyer / Borrower', icon: '🏠' },
                { key: 'seller', label: 'Seller', icon: '🤝' },
                { key: 'lender', label: 'Lender', icon: '🏦' },
                { key: 'titleCompany', label: 'Title Company', icon: '📋' },
              ].map(s => (
                <StakeholderCard
                  key={s.key}
                  label={s.label}
                  icon={s.icon}
                  value={stakeholders[s.key]}
                  onChange={val => setStakeholder(s.key, val)}
                />
              ))}
            </div>

            <div className="m14-coord-checklist">
              <h4>Pre-Closing Coordination Checklist</h4>
              {[
                'Final walkthrough scheduled with buyer (24–48 hrs before closing)',
                'Seller confirms property will be vacant and in agreed condition',
                'Lender confirms clear-to-close (CTC) issued',
                'Title company confirms all Schedule B-I exceptions cleared',
                'Wire instructions verified via phone (fraud prevention)',
                'Notary or closing attorney scheduled',
                'Keys and access codes arranged for transfer',
                'Utilities transfer scheduled for closing date',
              ].map((item, i) => (
                <label key={i} className="m14-coord-item">
                  <input
                    type="checkbox"
                    checked={!!(notes['coord_' + i])}
                    onChange={e => setNote('coord_' + i, e.target.checked ? '1' : '')}
                  />
                  <span>{item}</span>
                </label>
              ))}
            </div>

            <div className="m14-form-group full">
              <label>Coordination Notes</label>
              <textarea className="m14-textarea" rows={3} value={notes.coordination || ''} onChange={e => setNote('coordination', e.target.value)} placeholder="Special scheduling notes, attorney contact, closing location…" />
            </div>
          </div>
        )}

        {/* ── FUNDS & SIGNING TAB ────────────────────────────── */}
        {activeTab === 'funds' && (
          <div className="m14-funds-panel">
            <div className="m14-section-header">
              <h3>Funds Verification &amp; Document Signing</h3>
              <p>Track wire transfers, confirm funds received, and manage the document signing process.</p>
            </div>

            <div className="m14-funds-cards">
              <div className="m14-fund-card">
                <div className="m14-fund-card-header">
                  <span className="m14-fund-icon">💸</span>
                  <span className="m14-fund-title">Buyer Wire (Cash to Close)</span>
                  <span className={`m14-fund-status ${completed.includes('wire_downpayment') ? 'received' : 'pending'}`}>
                    {completed.includes('wire_downpayment') ? 'Received' : 'Pending'}
                  </span>
                </div>
                <div className="m14-fund-amount">{money(wireAmt)}</div>
                <input
                  className="m14-input"
                  value={wireAmt}
                  onChange={e => setWireAmt(e.target.value)}
                  placeholder="Exact wire amount per CD"
                />
                <p className="m14-fund-note">Verify amount matches Closing Disclosure exactly. Confirm receipt with title company.</p>
              </div>

              <div className="m14-fund-card">
                <div className="m14-fund-card-header">
                  <span className="m14-fund-icon">🏛️</span>
                  <span className="m14-fund-title">Loan Proceeds (Lender Wire)</span>
                  <span className={`m14-fund-status ${completed.includes('wire_lender') ? 'received' : 'pending'}`}>
                    {completed.includes('wire_lender') ? 'Received' : 'Pending'}
                  </span>
                </div>
                <div className="m14-fund-amount">{money(loanAmt)}</div>
                <input className="m14-input" value={money(loanAmt)} readOnly />
                <p className="m14-fund-note">Lender wires loan proceeds to escrow. Confirm with title company before disbursement.</p>
              </div>

              <div className="m14-fund-card">
                <div className="m14-fund-card-header">
                  <span className="m14-fund-icon">💰</span>
                  <span className="m14-fund-title">Seller Payout</span>
                  <span className={`m14-fund-status ${completed.includes('funds_disbursed') ? 'disbursed' : 'pending'}`}>
                    {completed.includes('funds_disbursed') ? 'Disbursed' : 'Pending'}
                  </span>
                </div>
                <div className="m14-fund-amount">{money(sellerPayout)}</div>
                <input
                  className="m14-input"
                  value={sellerPayout}
                  onChange={e => setSellerPayout(e.target.value)}
                  placeholder="Net seller proceeds after payoffs"
                />
                <p className="m14-fund-note">Seller net proceeds after mortgage payoff, commissions, and closing costs.</p>
              </div>
            </div>

            <div className="m14-signing-block">
              <h4>Document Signing</h4>
              <div className="m14-signing-docs">
                {[
                  { label: 'Promissory Note', icon: '📝', required: true },
                  { label: 'Deed of Trust / Mortgage', icon: '🏠', required: true },
                  { label: 'HUD-1 / Closing Statement', icon: '📋', required: true },
                  { label: 'Grant Deed / Warranty Deed', icon: '📜', required: true },
                  { label: 'Transfer Tax Affidavit', icon: '📄', required: true },
                  { label: 'Bill of Sale (personal prop.)', icon: '📃', required: false },
                  { label: 'Assignment of Leases & Rents', icon: '🔑', required: true },
                  { label: 'FIRPTA Affidavit', icon: '⚖️', required: false },
                ].map(doc => (
                  <label key={doc.label} className="m14-signing-doc">
                    <input
                      type="checkbox"
                      checked={!!(notes['doc_' + doc.label])}
                      onChange={e => setNote('doc_' + doc.label, e.target.checked ? '1' : '')}
                    />
                    <span className="m14-signing-doc-icon">{doc.icon}</span>
                    <span className="m14-signing-doc-label">{doc.label}</span>
                    {doc.required && <span className="m14-required-tag">required</span>}
                  </label>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* ── RECORDING & CLOSE TAB ──────────────────────────── */}
        {/* ── CLOSING COSTS BREAKDOWN ────────────────────────── */}
        {activeTab === 'costs' && (
          <div className="m14-costs-panel">
            <div className="m14-section-header">
              <h3>Itemized Closing Cost Breakdown</h3>
              <p>Edit individual line items to match your actual Closing Disclosure. Compare against the flat 2.5% estimate.</p>
            </div>
            <div className="m14-costs-summary">
              {[
                { label: 'Down Payment', val: money(downPmt), note: `${Math.round((downPmt / price) * 100)}% LTV` },
                { label: 'Itemized Costs', val: money(totalItemizedCost), note: `${((totalItemizedCost / price) * 100).toFixed(2)}% of price` },
                { label: 'Total Cash to Close', val: money(downPmt + totalItemizedCost), note: `vs est. ${money(cashToClose)} (${Math.round(((downPmt + totalItemizedCost - cashToClose) / cashToClose) * 100) >= 0 ? '+' : ''}${Math.round(((downPmt + totalItemizedCost - cashToClose) / cashToClose) * 100)}%)` },
              ].map((c) => (
                <div key={c.label} className="m14-costs-sum-card">
                  <div className="m14-costs-sum-label">{c.label}</div>
                  <div className="m14-costs-sum-val">{c.val}</div>
                  <div className="m14-costs-sum-note">{c.note}</div>
                </div>
              ))}
            </div>
            {itemizedCostCats.map((cat) => (
              <div key={cat} className="m14-costs-cat">
                <div className="m14-costs-cat-header">
                  <span>{cat}</span>
                  <span>{money(costItems.filter((c) => c.cat === cat).reduce((s, c) => s + Number(c.amount), 0))}</span>
                </div>
                {costItems.filter((c) => c.cat === cat).map((item) => (
                  <div key={item.id} className="m14-costs-row">
                    <span className="m14-costs-row-label">{item.label}</span>
                    <input
                      type="number"
                      className="m14-costs-input"
                      value={item.amount}
                      onChange={(e) => setCostItems((prev) => prev.map((c) => c.id === item.id ? { ...c, amount: Number(e.target.value) } : c))}
                    />
                  </div>
                ))}
              </div>
            ))}
          </div>
        )}

        {/* ── WIRE FRAUD CHECKLIST ───────────────────────────── */}
        {activeTab === 'wiresecurity' && (
          <div className="m14-wire-panel">
            <div className="m14-section-header">
              <h3>Wire Fraud Prevention Checklist</h3>
              <p>Complete every step before wiring any funds. Wire fraud losses in real estate are rarely recoverable.</p>
            </div>
            <div className="m14-wire-alert">
              <span className="m14-wire-alert-icon">⚠️</span>
              <div>
                <strong>FBI Warning:</strong> Real estate wire fraud exceeded $446M in losses last year. Criminals intercept closing emails and send fake wire instructions with legitimate-looking formatting.
              </div>
            </div>
            <div className="m14-wire-progress-wrap">
              <div className="m14-wire-progress" style={{ width: `${Math.round((wireChecks.length / WIRE_STEPS.length) * 100)}%` }} />
            </div>
            <div className="m14-wire-steps">
              {WIRE_STEPS.map((step, i) => (
                <label key={step.id} className={`m14-wire-step ${wireChecks.includes(step.id) ? 'checked' : ''}`}>
                  <input
                    type="checkbox"
                    checked={wireChecks.includes(step.id)}
                    onChange={() => setWireChecks((prev) => prev.includes(step.id) ? prev.filter((x) => x !== step.id) : [...prev, step.id])}
                  />
                  <span className="m14-wire-step-num">{i + 1}</span>
                  <span className="m14-wire-step-text">{step.label}</span>
                </label>
              ))}
            </div>
            {wireChecks.length === WIRE_STEPS.length && (
              <div className="m14-wire-cleared"> All verification steps complete. Safe to initiate wire transfer.</div>
            )}
            <div className="m14-wire-redflags">
              <h4>🚩 Red Flags — Stop and Verify</h4>
              {[
                'Wire instructions arrived by email only, with no phone confirmation',
                'Routing or account number changed from a previous version',
                'Urgency pressure: "Wire today or the deal falls through"',
                'Email domain is slightly misspelled (e.g., firstamerican-title.com)',
                'Instructions came from a personal email address instead of company domain',
              ].map((flag) => (
                <div key={flag} className="m14-wire-redflag-item">🚩 {flag}</div>
              ))}
            </div>
          </div>
        )}

      </div>
      <div className="module-bottom-advance">
        <button
          className={`m14-complete-btn ${deedRecorded && !saving ? 'ready' : 'disabled'}`}
          onClick={handleComplete}
          disabled={!deedRecorded || saving}
        >
          {saving ? 'Recording Completion...' : 'Close Deal & Advance to Module 15'}
        </button>
      </div>
    </div>
  )
}
